<template>
    <section ref="page" class="setup-page" :aria-label="label" tabindex="-1">
        <slot name="status" />
        <div ref="body" class="setup-body">
            <BaseLayout gap="none">
                <!-- The overview provides its own heading. Show the page heading only inside steps to avoid
                     duplicate titles.
                     -->
                <header v-if="!showOverview" class="page-head">
                    <div class="page-head-row">
                        <h1 class="page-title">Coaching setup</h1>
                        <BaseButton theme="tertiary" size="sm" class="escape-action" @click="emit('escape')">
                            <i class="bi bi-arrow-left" aria-hidden="true"></i>
                            All asks
                        </BaseButton>
                    </div>
                    <hr class="page-rule" />
                </header>

                <!-- Closing a step returns to the overview because setup occupies its own tab. -->
                <div v-if="showOverview" class="overview">
                    <slot name="overview" />
                </div>

                <div v-else class="workspace">
                    <!-- Omit the rail count because the overview already shows remaining work. -->
                    <aside class="sections">
                        <slot name="sections" />
                    </aside>
                    <section ref="stepContent" class="step-content" aria-label="Current setup step" tabindex="-1">
                        <slot name="step" />
                    </section>
                </div>
            </BaseLayout>
        </div>
    </section>
</template>

<script setup lang="ts">
/**
 * Share layout with the fixture gallery. Reusing GuidedSetupPage would invoke live endpoints through
 * GuidedSetupStepRunner.
 */
import BaseButton from "~vue/components/BaseButton.vue";
import BaseLayout from "~vue/components/BaseLayout.vue";
import { nextTick, useTemplateRef } from "vue";

defineProps<{
    label: string;
    showOverview: boolean;
}>();

const emit = defineEmits<{ escape: [] }>();

const page = useTemplateRef<HTMLElement>("page");
const body = useTemplateRef<HTMLElement>("body");
const stepContent = useTemplateRef<HTMLElement>("stepContent");

function focusPage(): void {
    page.value?.focus({ preventScroll: true });
}

async function resetScroll(): Promise<void> {
    await nextTick();
    body.value?.scrollTo({ top: 0 });
    stepContent.value?.focus({ preventScroll: true });
}

defineExpose({ focusPage, resetScroll });
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .setup-page {
        @apply relative flex min-h-0 flex-1 flex-col overflow-hidden bg-surface text-ink focus-visible:outline-none;
    }

    .setup-body {
        @apply min-h-0 flex-1 overflow-y-auto pb-8;
    }

    .page-head {
        @apply flex flex-col gap-3 pt-6 pb-7;
    }

    .page-head-row {
        @apply flex items-center justify-between gap-4;
    }

    .page-title {
        @apply display-serif text-ink;
    }

    .escape-action {
        @apply -mr-2 inline-flex shrink-0 items-center gap-2;
    }

    .page-rule {
        @apply border-t border-border;
    }

    .overview {
        @apply pt-8;
    }

    .workspace {
        @apply grid min-w-0 grid-cols-1 items-start gap-5 lg:grid-cols-[190px_minmax(0,1fr)] lg:gap-10;
    }

    .sections {
        @apply min-w-0 lg:sticky lg:top-6 lg:self-start;
    }

    /* Set width here for all steps; independent step widths previously misaligned their content. */
    .step-content {
        @apply min-w-0 max-w-168 outline-none;
    }
}
</style>
